import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Page, PageHeader } from "../components/PageHeader";
import DataTable from "../components/DataTable";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { naira, compactNaira, titleCase, fmtDateTime, fmtDate } from "../lib/format";
import StockActions from "../components/inventory/StockActions";
import BomManager from "../components/inventory/BomManager";
import { AlertTriangle, TrendingDown, PackageX, Clock, BellRing, Settings2, ScrollText } from "lucide-react";

const BURG = { background: "#8B1E1E" };
const LEVEL_COLOR = { critical: "#A52222", warning: "#B7791F", informational: "#8a6f5c", ok: "#4F7A52" };

export default function Inventory() {
  const { user, can } = useAuth();
  const scopedLoc = user && user.location_id && !user.is_admin ? user.location_id : null;
  const [overview, setOverview] = useState(null);
  const [balances, setBalances] = useState([]);
  const [movements, setMovements] = useState([]);
  const [batches, setBatches] = useState([]);
  const [grid, setGrid] = useState({ columns: [], rows: [] });
  const [alerts, setAlerts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [reasonCodes, setReasonCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moveType, setMoveType] = useState("all");
  const [ledger, setLedger] = useState(null);
  const [thresh, setThresh] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/inventory/overview"), api.get("/inventory/balances"), api.get("/inventory/movements"),
      api.get("/inventory/batches"), api.get("/inventory/by-location"), api.get("/inventory/alerts?status=open"),
      api.get("/inventory/items"),
    ]).then(([o, b, m, bt, g, a, it]) => {
      setOverview(o); setBalances(b); setMovements(m); setBatches(bt); setGrid(g); setAlerts(a); setItems(it);
    }).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/warehouses").then(setWarehouses).catch(() => {});
    api.get("/inventory/reason-codes").then(setReasonCodes).catch(() => {});
  }, []);

  const rawItems = items;
  const openLedger = async (pid) => { try { setLedger(await api.get(`/inventory/ledger/${pid}`)); } catch (e) { toast.error(formatApiError(e)); } };
  const saveThresh = async () => {
    try { await api.put(`/inventory/thresholds/${thresh.id}`, thresh); toast.success("Thresholds saved"); setThresh(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const ackAlert = async (a, status) => { try { await api.post(`/inventory/alerts/${a.id}/status`, { status }); toast.success("Alert " + status); load(); } catch (e) { toast.error(formatApiError(e)); } };

  const Stat = ({ icon: Icon, label, value, accent }) => (
    <div className="rounded-xl border p-5 shadow-sm" style={{ background: "#FAF7F0", borderColor: "#ECE4D8" }}>
      <div className="flex items-center gap-2 text-sm" style={{ color: "#765A4A" }}>{Icon && <Icon size={15} style={{ color: accent || "#8B1E1E" }} />}{label}</div>
      <div className="text-2xl font-brand font-bold mt-1" style={{ color: accent || "#3B2722" }}>{value}</div>
    </div>
  );

  const levelPill = (lvl) => <span className="text-xs px-2 py-0.5 rounded font-medium capitalize" style={{ background: `${LEVEL_COLOR[lvl]}1a`, color: LEVEL_COLOR[lvl] }}>{lvl}</span>;

  const balCols = [
    { key: "sku", label: "SKU", render: (r) => <span className="font-mono text-xs" style={{ color: "#a58f7c" }}>{r.sku}</span> },
    { key: "name", label: "Item", render: (r) => <span className="font-medium" style={{ color: "#3B2722" }}>{r.name}</span> },
    { key: "category_name", label: "Category" },
    { key: "quantity", label: "On Hand", align: "right", render: (r) => <span className="font-semibold" style={{ color: LEVEL_COLOR[r.level] }}>{r.quantity}{r.unit ? ` ${r.unit}` : ""}</span> },
    { key: "level", label: "Level", render: (r) => levelPill(r.level) },
    { key: "value", label: "Value", align: "right", render: (r) => naira(r.value) },
    { key: "actions", label: "", sortable: false, render: (r) => (
      <div className="flex gap-1 justify-end">
        <Button size="sm" variant="ghost" onClick={() => openLedger(r.product_id)} data-testid={`ledger-${r.product_id}`}><ScrollText size={14} /></Button>
        {can("inventory", "edit") && <Button size="sm" variant="ghost" onClick={() => setThresh({ id: r.product_id, name: r.name, threshold_critical: r.threshold_critical, threshold_warning: r.threshold_warning, threshold_info: r.threshold_info, reorder_qty: r.reorder_level })} data-testid={`thresh-${r.product_id}`}><Settings2 size={14} /></Button>}
      </div>) },
  ];
  const moveCols = [
    { key: "date", label: "Date", render: (r) => fmtDateTime(r.date) },
    { key: "product_name", label: "Item" },
    { key: "warehouse_name", label: "Warehouse" },
    { key: "movement_type", label: "Type", render: (r) => <span className="text-xs px-2 py-0.5 rounded" style={{ background: "#F3ECE2", color: "#765A4A" }}>{titleCase(r.movement_type)}</span> },
    { key: "quantity", label: "Qty", align: "right", render: (r) => <span className="font-semibold" style={{ color: r.quantity < 0 ? "#A52222" : "#4F7A52" }}>{r.quantity > 0 ? "+" : ""}{r.quantity}{r.shortage ? " ⚠" : ""}</span> },
    { key: "unit_cost", label: "Unit Cost", align: "right", render: (r) => naira(r.unit_cost) },
    { key: "reference", label: "Ref", render: (r) => <span className="text-xs" style={{ color: "#a58f7c" }}>{r.reference}</span> },
  ];
  const batchCols = [
    { key: "batch_no", label: "Batch", render: (r) => <span className="font-mono text-xs" style={{ color: "#a58f7c" }}>{r.batch_no}</span> },
    { key: "product_name", label: "Item", render: (r) => <span className="font-medium" style={{ color: "#3B2722" }}>{r.product_name}</span> },
    { key: "warehouse_name", label: "Warehouse" },
    { key: "remaining_qty", label: "Remaining", align: "right", render: (r) => <span className="font-semibold">{r.remaining_qty} / {r.received_qty}</span> },
    { key: "unit_cost", label: "Unit Cost", align: "right", render: (r) => naira(r.unit_cost) },
    { key: "value", label: "Value", align: "right", render: (r) => naira(r.value) },
    { key: "expiry_date", label: "Expiry", render: (r) => r.expiry_date ? <span style={{ color: r.expired ? "#A52222" : "#765A4A" }}>{fmtDate(r.expiry_date)}{r.expired ? " (expired)" : ""}</span> : "—" },
  ];
  const alertCols = [
    { key: "level", label: "Level", render: (r) => levelPill(r.level) },
    { key: "product_name", label: "Item", render: (r) => <span className="font-medium" style={{ color: "#3B2722" }}>{r.product_name}</span> },
    { key: "location_name", label: "Location" },
    { key: "current_quantity", label: "On Hand", align: "right" },
    { key: "threshold_value", label: "Threshold", align: "right" },
    { key: "suggested_reorder_qty", label: "Suggested Reorder", align: "right" },
    { key: "supplier_name", label: "Supplier" },
    { key: "actions", label: "", sortable: false, render: (r) => can("inventory", "edit") && (
      <Button size="sm" variant="outline" onClick={() => ackAlert(r, "acknowledged")} data-testid={`ack-${r.id}`}>Acknowledge</Button>) },
  ];

  const filteredMoves = moveType === "all" ? movements : movements.filter((m) => m.movement_type === moveType);
  const moveTypes = ["all", ...Array.from(new Set(movements.map((m) => m.movement_type)))];

  return (
    <Page>
      <PageHeader title="Inventory & Recipes" subtitle="FIFO stock ledger, batches, recipes (BOM) & reorder alerts — quantity derived from the movement ledger"
        actions={can("inventory", "create") && <StockActions warehouses={warehouses} items={rawItems} reasonCodes={reasonCodes} scopedLoc={scopedLoc} onDone={load} />} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat icon={undefined} label="Inventory Value" value={compactNaira(overview?.total_value || 0)} />
        <Stat icon={AlertTriangle} label="Critical Items" value={overview?.critical || 0} accent="#A52222" />
        <Stat icon={TrendingDown} label="Warnings" value={overview?.warning || 0} accent="#B7791F" />
        <Stat icon={BellRing} label="Open Alerts" value={overview?.alerts_open || 0} accent="#8B1E1E" />
        <Stat icon={PackageX} label="Out of Stock" value={overview?.out_of_stock || 0} accent="#A52222" />
        <Stat icon={TrendingDown} label="Negative Stock" value={overview?.negative_stock || 0} accent="#A52222" />
        <Stat icon={Clock} label="Expiring ≤14d" value={overview?.expiring_soon || 0} accent="#B7791F" />
        <Stat label="Tracked Items" value={overview?.item_count || 0} />
      </div>

      <Tabs defaultValue="stock">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="stock" data-testid="tab-stock">Stock Levels</TabsTrigger>
          <TabsTrigger value="location" data-testid="tab-location">By Location</TabsTrigger>
          <TabsTrigger value="batches" data-testid="tab-batches">Batches</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Movement Ledger</TabsTrigger>
          <TabsTrigger value="bom" data-testid="tab-bom">Recipes (BOM)</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">Reorder Alerts{alerts.length ? ` (${alerts.length})` : ""}</TabsTrigger>
        </TabsList>

        <TabsContent value="stock"><DataTable columns={balCols} rows={balances} loading={loading} searchKeys={["name", "sku", "category_name"]} testIdPrefix="balances" emptyText="No stock items" /></TabsContent>

        <TabsContent value="location">
          <div className="rounded-xl border shadow-sm overflow-x-auto" style={{ background: "#FAF7F0", borderColor: "#ECE4D8" }}>
            <table className="w-full text-sm"><thead><tr className="border-b" style={{ background: "#EFE7D6", borderColor: "#F0E9DD" }}>
              <th className="text-left px-4 py-3 text-xs uppercase font-semibold" style={{ color: "#8a6f5c" }}>Item</th>
              {grid.columns.map((c) => <th key={c.id} className="text-right px-4 py-3 text-xs uppercase font-semibold" style={{ color: "#8a6f5c" }}>{c.name}</th>)}
              <th className="text-right px-4 py-3 text-xs uppercase font-semibold" style={{ color: "#8a6f5c" }}>Total</th>
            </tr></thead><tbody>
              {grid.rows.map((r) => (<tr key={r.product_id} className="border-b" style={{ borderColor: "#F5EFE5" }} data-testid={`grid-row-${r.product_id}`}>
                <td className="px-4 py-3 font-medium" style={{ color: "#3B2722" }}>{r.name} <span className="text-xs" style={{ color: "#a58f7c" }}>{r.unit}</span></td>
                {grid.columns.map((c) => <td key={c.id} className="px-4 py-3 text-right" style={{ color: r.cells[c.id] < 0 ? "#A52222" : "#3B2722" }}>{r.cells[c.id]}</td>)}
                <td className="px-4 py-3 text-right font-semibold">{r.total}</td>
              </tr>))}
              {!grid.rows.length && <tr><td colSpan={grid.columns.length + 2} className="px-4 py-16 text-center text-sm" style={{ color: "#a58f7c" }}>No items</td></tr>}
            </tbody></table>
          </div>
        </TabsContent>

        <TabsContent value="batches"><DataTable columns={batchCols} rows={batches} loading={loading} searchKeys={["product_name", "batch_no", "warehouse_name"]} testIdPrefix="batches-tbl" emptyText="No active batches" /></TabsContent>

        <TabsContent value="ledger">
          <DataTable columns={moveCols} rows={filteredMoves} loading={loading} searchKeys={["product_name", "sku", "reference"]} testIdPrefix="movements" emptyText="No movements"
            toolbar={<Select value={moveType} onValueChange={setMoveType}><SelectTrigger className="w-44" data-testid="move-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>{moveTypes.map((t) => <SelectItem key={t} value={t}>{t === "all" ? "All types" : titleCase(t)}</SelectItem>)}</SelectContent></Select>} />
        </TabsContent>

        <TabsContent value="bom"><BomManager /></TabsContent>

        <TabsContent value="alerts"><DataTable columns={alertCols} rows={alerts} loading={loading} searchKeys={["product_name", "location_name", "supplier_name"]} testIdPrefix="alerts" emptyText="No open reorder alerts" /></TabsContent>
      </Tabs>

      {/* Item ledger / audit trace */}
      <Dialog open={!!ledger} onOpenChange={(o) => !o && setLedger(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-brand">{ledger?.product?.name} — Audit Trace</DialogTitle>
            <DialogDescription>Every movement that explains the current position. The ledger is the source of truth.</DialogDescription></DialogHeader>
          <div className="flex items-center justify-between rounded-lg p-3 mb-2" style={{ background: "#F7FBF7", border: "1px solid #cfe3d1" }}>
            <span className="text-sm" style={{ color: "#4F7A52" }}>Current balance</span>
            <span className="font-brand font-bold text-lg" style={{ color: "#3B2722" }}>{ledger?.current_balance}</span></div>
          <div className="flex flex-wrap gap-2 mb-3">{Object.entries(ledger?.summary || {}).map(([k, v]) => (
            <span key={k} className="text-xs px-2 py-1 rounded" style={{ background: "#F3ECE2", color: "#765A4A" }}>{titleCase(k)}: <b>{v}</b></span>))}</div>
          <div className="space-y-1 max-h-80 overflow-y-auto">{(ledger?.trace || []).map((t, i) => (
            <div key={i} className="flex items-center justify-between text-sm border-b py-1.5" style={{ borderColor: "#F5EFE5" }}>
              <div><span className="text-xs px-1.5 py-0.5 rounded mr-2" style={{ background: "#F3ECE2", color: "#765A4A" }}>{titleCase(t.movement_type)}</span>
                <span style={{ color: "#a58f7c" }}>{fmtDateTime(t.date)}</span>{t.shortage && <span className="ml-1" style={{ color: "#A52222" }}>⚠ shortage</span>}</div>
              <div className="flex items-center gap-4"><span style={{ color: t.quantity < 0 ? "#A52222" : "#4F7A52" }}>{t.quantity > 0 ? "+" : ""}{t.quantity}</span>
                <span className="font-medium" style={{ color: "#3B2722" }}>= {t.balance}</span></div>
            </div>))}</div>
        </DialogContent>
      </Dialog>

      {/* Thresholds config */}
      <Dialog open={!!thresh} onOpenChange={(o) => !o && setThresh(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-brand">Reorder Thresholds — {thresh?.name}</DialogTitle>
            <DialogDescription>Absolute on-hand levels (not percentages) that trigger each alert tier.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-1">
            <div className="space-y-1.5"><Label style={{ color: "#8a6f5c" }}>Informational at ≤</Label><Input type="number" value={thresh?.threshold_info ?? ""} onChange={(e) => setThresh((s) => ({ ...s, threshold_info: e.target.value }))} data-testid="th-info" /></div>
            <div className="space-y-1.5"><Label style={{ color: "#B7791F" }}>Warning at ≤</Label><Input type="number" value={thresh?.threshold_warning ?? ""} onChange={(e) => setThresh((s) => ({ ...s, threshold_warning: e.target.value }))} data-testid="th-warning" /></div>
            <div className="space-y-1.5"><Label style={{ color: "#A52222" }}>Critical at ≤</Label><Input type="number" value={thresh?.threshold_critical ?? ""} onChange={(e) => setThresh((s) => ({ ...s, threshold_critical: e.target.value }))} data-testid="th-critical" /></div>
            <div className="space-y-1.5"><Label>Suggested reorder qty</Label><Input type="number" value={thresh?.reorder_qty ?? ""} onChange={(e) => setThresh((s) => ({ ...s, reorder_qty: e.target.value }))} data-testid="th-reorder" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setThresh(null)}>Cancel</Button>
            <Button onClick={saveThresh} data-testid="th-save" style={BURG} className="text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
